import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

export type EmissionRecord = Tables<'emissions'>;

export interface EmissionWithRelations extends EmissionRecord {
  ride?: {
    id: string;
    booking?: {
      id: string;
      start_time: string;
      end_time: string;
      ride_type: string;
      status: string;
      start_location_type: string;
      end_location_type: string;
      start_location_id: string | null;
      end_location_id: string | null;
      user?: {
        username: string;
      };
      bike?: {
        serial_number: string;
      };
      start_site?: {
        name: string;
      };
      end_site?: {
        name: string;
      };
    };
  };
}

/**
 * Fetches all emissions records with related data
 */
export const fetchEmissions = async (): Promise<EmissionWithRelations[]> => {
  const { data, error } = await supabase
    .from('emissions')
    .select(`
      *,
      ride:rides(
        id,
        booking:bookings(
          id,
          start_time,
          end_time,
          ride_type,
          status,
          start_location_type,
          end_location_type,
          start_location_id,
          end_location_id,
          user:users(username),
          bike:bikes(serial_number),
          start_site:sites!bookings_start_location_id_fkey(name),
          end_site:sites!bookings_end_location_id_fkey(name)
        )
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching emissions:', error);
    throw error;
  }

  return data || [];
};

/**
 * Fetches a single emission record by ID
 */
export const fetchEmissionById = async (id: string): Promise<EmissionWithRelations | null> => {
  const { data, error } = await supabase
    .from('emissions')
    .select(`
      *,
      ride:rides(
        id,
        booking:bookings(
          id,
          start_time,
          end_time,
          ride_type,
          status,
          start_location_type,
          end_location_type,
          start_location_id,
          end_location_id,
          user:users(username),
          bike:bikes(serial_number),
          start_site:sites!bookings_start_location_id_fkey(name),
          end_site:sites!bookings_end_location_id_fkey(name)
        )
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching emission:', error);
    throw error;
  }

  return data;
};

/**
 * Fetches emissions records for a specific ride
 */
export const fetchEmissionsByRideId = async (rideId: string): Promise<EmissionWithRelations[]> => {
  const { data, error } = await supabase
    .from('emissions')
    .select(`
      *,
      ride:rides(
        id,
        booking:bookings(
          id,
          start_time,
          end_time,
          ride_type,
          status,
          start_location_type,
          end_location_type,
          start_location_id,
          end_location_id,
          user:users(username),
          bike:bikes(serial_number),
          start_site:sites!bookings_start_location_id_fkey(name),
          end_site:sites!bookings_end_location_id_fkey(name)
        )
      )
    `)
    .eq('ride_id', rideId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching emissions by ride:', error);
    throw error;
  }

  return data || [];
};

/**
 * Creates a new emission record
 */
export const createEmission = async (emission: Omit<EmissionRecord, 'id' | 'created_at'>): Promise<EmissionRecord> => {
  const { data, error } = await supabase
    .from('emissions')
    .insert(emission)
    .select()
    .single();

  if (error) {
    console.error('Error creating emission:', error);
    throw error;
  }

  return data;
};

/**
 * Updates an existing emission record
 */
export const updateEmission = async (id: string, updates: Partial<EmissionRecord>): Promise<EmissionRecord> => {
  const { data, error } = await supabase
    .from('emissions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating emission:', error);
    throw error;
  }

  return data;
};

/**
 * Deletes an emission record
 */
export const deleteEmission = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('emissions')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting emission:', error);
    throw error;
  }
};

/**
 * Calculates total emissions statistics
 */
export const calculateEmissionsStats = async (): Promise<{
  totalCO2Saved: number;
  totalCH4Saved: number;
  totalN2OSaved: number;
  totalCO2Emitted: number;
  totalCH4Emitted: number;
  totalN2OEmitted: number;
  totalCO2eCO2: number;
  totalCO2eCH4: number;
  totalCO2eN2O: number;
  totalCO2eEquivalent: number;
}> => {
  const { data, error } = await supabase
    .from('emissions')
    .select('kg_co2_saved, kg_ch4_saved, kg_n2o_saved, kg_co2_emitted, kg_ch4_emitted, kg_n2o_emitted, co2e_co2, co2e_ch4, co2e_n2o');

  if (error) {
    console.error('Error calculating emissions stats:', error);
    throw error;
  }

  const stats = data.reduce((acc, emission) => {
    acc.totalCO2Saved += emission.kg_co2_saved || 0;
    acc.totalCH4Saved += emission.kg_ch4_saved || 0;
    acc.totalN2OSaved += emission.kg_n2o_saved || 0;
    acc.totalCO2Emitted += emission.kg_co2_emitted || 0;
    acc.totalCH4Emitted += emission.kg_ch4_emitted || 0;
    acc.totalN2OEmitted += emission.kg_n2o_emitted || 0;
    acc.totalCO2eCO2 += emission.co2e_co2 || 0;
    acc.totalCO2eCH4 += emission.co2e_ch4 || 0;
    acc.totalCO2eN2O += emission.co2e_n2o || 0;
    return acc;
  }, {
    totalCO2Saved: 0,
    totalCH4Saved: 0,
    totalN2OSaved: 0,
    totalCO2Emitted: 0,
    totalCH4Emitted: 0,
    totalN2OEmitted: 0,
    totalCO2eCO2: 0,
    totalCO2eCH4: 0,
    totalCO2eN2O: 0,
    totalCO2eEquivalent: 0,
  });

  // Calculate total CO2 equivalent
  stats.totalCO2eEquivalent = stats.totalCO2eCO2 + stats.totalCO2eCH4 + stats.totalCO2eN2O;

  return stats;
}; 